/**
 * The HTTP edge.
 *
 * Two jobs and nothing else: turn a non-2xx into a typed error that carries the
 * parsed body (the 409 from a lost claim names the winner, and a toast that
 * cannot read it is useless), and parse every response through the shared zod
 * schema so a server that starts sending a differently shaped row fails loudly
 * here rather than rendering a blank cell three components away.
 */

import type { z } from 'zod';

export class HTTPError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'HTTPError';
  }
}

/** A response that did not survive its schema. Never retried — it will not fix itself. */
export class ContractError extends Error {
  constructor(
    readonly url: string,
    readonly issues: string,
  ) {
    super(`${url} did not match its schema: ${issues}`);
    this.name = 'ContractError';
  }
}

type Json = Record<string, unknown> | unknown[] | null;

async function request(method: string, path: string, body?: Json): Promise<unknown> {
  const response = await fetch(path, {
    method,
    // Same-origin in dev (Vite proxies) and in production (Fastify serves the
    // bundle), so the session cookie rides along without any CORS dance.
    credentials: 'same-origin',
    headers:
      body === undefined
        ? { accept: 'application/json' }
        : {
            accept: 'application/json',
            'content-type': 'application/json',
          },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await response.text();
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const message =
      typeof parsed === 'object' && parsed !== null && 'error' in parsed
        ? String(parsed.error)
        : `${method} ${path} failed with ${String(response.status)}`;
    throw new HTTPError(response.status, parsed, message);
  }

  return parsed;
}

function check<S extends z.ZodTypeAny>(schema: S, url: string, value: unknown): z.infer<S> {
  const result = schema.safeParse(value);
  if (!result.success) {
    throw new ContractError(
      url,
      result.error.issues
        .slice(0, 4)
        .map((i) => `${i.path.join('.')}: ${i.message}`)
        .join('; '),
    );
  }
  return result.data as z.infer<S>;
}

export const api = {
  async get<S extends z.ZodTypeAny>(path: string, schema: S): Promise<z.infer<S>> {
    return check(schema, path, await request('GET', path));
  },

  async post<S extends z.ZodTypeAny>(path: string, schema: S, body?: Json): Promise<z.infer<S>> {
    return check(schema, path, await request('POST', path, body ?? {}));
  },

  async put<S extends z.ZodTypeAny>(path: string, schema: S, body: Json): Promise<z.infer<S>> {
    return check(schema, path, await request('PUT', path, body));
  },
};

/** Build a query string, skipping anything undefined. */
export function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded === '' ? '' : `?${encoded}`;
}
